/* Free gift at a cart threshold (the $150 towel), owned by the cart drawer and
   loaded on every page by snippets/cart-drawer-native.liquid when
   settings.cart_gift_enabled is on.

   Shopify's automatic Buy X Get Y discount makes the gift line free at
   checkout but never adds it, so this script keeps the cart honest:

   - after any cart write it can see (Dawn's product-form and cart.js, the
     bundle builder, an app's fetch or XHR) it reads /cart.js and either adds
     the gift line (subtotal at or over the threshold, no gift-product line in
     the cart) or removes it (subtotal fell under, or the shopper is over the
     line but the gift line is not ours);
   - a gift line is one carrying the `_wg_gift` line property, and only lines
     with that property are ever removed; a towel the shopper put in the cart
     themselves is left alone, and counts as the gift (the discount frees one);
   - the shopper can decline: "No thanks" on the gift line removes it and holds
     it off for the rest of the session (sessionStorage);
   - the gift line is pinned to quantity 1;
   - the gift only exists for shoppers in config.countries (the theme setting
     cart_gift_countries, default US: the towel's shipping profile is US-only
     and checkout refuses the whole order otherwise). The snippet already
     answers for the market the shopper picked (config.regionAllowed); this
     script adds the IP side from assets/wg-region.js (window.WGRegion, the
     shared lookup every region-aware piece of the theme uses; a private
     fallback does the same fetch if that script is missing). Outside the
     list nothing is ever added and any gift line is removed. The lookup
     FAILS OPEN: no answer, a slow answer (2.5 s) or a blocked request leaves
     the gift on, so a lookup hiccup never costs a US shopper the gift.
     ?wg_region=CA seeds the shared session cache for QA.
     html[data-wg-gift-region] is "on" or "off" once known; CSS repaints the
     drawer band from it.

   Threshold arithmetic matches the discount: original line prices, gift cards
   and the gift product itself excluded. Exposed as window.WGCartGift
   {config, eligibleSubtotal(cart), reconcile(), regionAllowed}; every
   reconcile dispatches `wg:cart` on document with the final cart so other
   components can re-render, and `wg:gift-region` fires once the region is known.
   Its own writes carry an X-WG-Gift header so the watcher ignores them. */
(function () {
  var configEl = document.querySelector('script[data-wg-cart-gift]');
  if (!configEl) return;
  var config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }
  /* Gift switched off while carts still hold a gift line from when it was on:
     the discount no longer frees it, so remove it and add nothing. */
  if (config && config.retired) {
    readCart()
      .then(function (cart) {
        var lines = cart.items.filter(isGiftLine);
        return lines.reduce(function (p, line) {
          return p.then(function () {
            return ownFetch('/cart/change.js', { id: line.key, quantity: 0 });
          });
        }, Promise.resolve()).then(readCart);
      })
      .then(function (cart) {
        syncDrawerRows(cart);
        document.dispatchEvent(new CustomEvent('wg:cart', { detail: cart }));
      })
      .catch(function (error) {
        console.warn('[cart-gift]', error);
      });
    return;
  }
  if (!config || !config.threshold || !config.variants || !config.variants.length) return;

  var DECLINE_KEY = 'wg_gift_declined:' + config.key;
  var chain = Promise.resolve(null);
  var pendingTimer = null;

  function declined() {
    try {
      return sessionStorage.getItem(DECLINE_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function setDeclined(on) {
    try {
      if (on) sessionStorage.setItem(DECLINE_KEY, '1');
      else sessionStorage.removeItem(DECLINE_KEY);
    } catch (e) {
      /* private mode: nothing to remember */
    }
  }

  /* ---- Region -------------------------------------------------------- */
  var REGION_KEY = 'wg_region'; /* shared with assets/wg-region.js */
  var REGION_TIMEOUT_MS = 2500;
  var regionOk; /* undefined until resolved */
  var regionPromise = null;

  function cachedCountry() {
    try {
      var seed = new URLSearchParams(window.location.search).get('wg_region');
      if (seed && /^[A-Za-z]{2}$/.test(seed.trim())) sessionStorage.setItem(REGION_KEY, seed.trim().toUpperCase());
      var iso = sessionStorage.getItem(REGION_KEY);
      return iso && /^[A-Z]{2}$/.test(iso) ? iso : null;
    } catch (e) {
      return null;
    }
  }

  function detectCountry() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var lookup = fetch(root + 'browsing_context_suggestions.json', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (data) {
        var handle = data && data.detected_values && data.detected_values.country && data.detected_values.country.handle;
        return typeof handle === 'string' && /^[A-Za-z]{2}$/.test(handle.trim()) ? handle.trim().toUpperCase() : null;
      })
      .catch(function () {
        return null;
      });
    var timeout = new Promise(function (resolve) {
      setTimeout(function () {
        resolve(null);
      }, REGION_TIMEOUT_MS);
    });
    return Promise.race([lookup, timeout]);
  }

  function allowedIn(iso) {
    if (config.regionAllowed === false) return false; /* market picked in the selector is off the list */
    var list = Array.isArray(config.countries) ? config.countries.filter(Boolean) : [];
    if (!list.length) return true;
    if (!iso) return true; /* unknown: fail open */
    return list.indexOf(iso) !== -1;
  }

  /* Resolves once per page to true/false; only a positive detection is cached. */
  function resolveRegion() {
    if (regionPromise) return regionPromise;
    var source;
    if (config.regionAllowed === false) {
      source = Promise.resolve(null);
    } else if (window.WGRegion && typeof window.WGRegion.ready === 'function') {
      source = window.WGRegion.ready();
    } else {
      var known = cachedCountry();
      source = known
        ? Promise.resolve(known)
        : detectCountry().then(function (iso) {
            if (iso) {
              try {
                sessionStorage.setItem(REGION_KEY, iso);
              } catch (e) {
                /* private mode */
              }
            }
            return iso;
          });
    }
    regionPromise = source.then(function (iso) {
      regionOk = allowedIn(iso);
      window.WGCartGift.regionAllowed = regionOk;
      document.documentElement.setAttribute('data-wg-gift-region', regionOk ? 'on' : 'off');
      document.dispatchEvent(new CustomEvent('wg:gift-region', { detail: { allowed: regionOk, country: iso } }));
      return regionOk;
    });
    return regionPromise;
  }

  function isGiftLine(item) {
    return !!(item.properties && item.properties._wg_gift);
  }

  /* Redo's package-protection line and gift cards never count, matching the
     buy-side collection of the discount. */
  function isExcluded(item) {
    if (isGiftLine(item) || item.gift_card) return true;
    if (item.handle === config.handle) return true;
    return /package_protection|shipping-insurance|package-protection/i.test((item.product_type || '') + ' ' + (item.handle || ''));
  }

  function eligibleSubtotal(cart) {
    return cart.items.reduce(function (sum, item) {
      return isExcluded(item) ? sum : sum + item.original_line_price;
    }, 0);
  }

  function ownFetch(url, body) {
    return fetch(url, {
      method: body ? 'POST' : 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-WG-Gift': '1',
      },
      body: body ? JSON.stringify(body) : undefined,
    }).then(function (r) {
      return r.json();
    });
  }

  function readCart() {
    return ownFetch('/cart.js');
  }

  function plan(cart) {
    var subtotal = eligibleSubtotal(cart);
    var giftLines = cart.items.filter(isGiftLine);
    var ownTowel = cart.items.some(function (item) {
      return !isGiftLine(item) && item.handle === config.handle;
    });
    var met = subtotal >= config.threshold;

    if (!met || declined() || ownTowel || regionOk === false) {
      return giftLines.map(function (line) {
        return { id: line.key, quantity: 0 };
      });
    }
    if (giftLines.length === 0) {
      return [{ add: true }];
    }
    var writes = [];
    giftLines.forEach(function (line, index) {
      var known = config.variants.some(function (v) {
        return v.id === line.variant_id;
      });
      if (index > 0 || !known) writes.push({ id: line.key, quantity: 0 });
      else if (line.quantity !== 1) writes.push({ id: line.key, quantity: 1 });
    });
    return writes;
  }

  function apply(writes) {
    return writes.reduce(function (p, write) {
      return p.then(function () {
        if (write.add) {
          var props = {};
          props._wg_gift = config.key;
          return ownFetch('/cart/add.js', {
            items: [{ id: config.defaultVariant, quantity: 1, properties: props }],
          });
        }
        return ownFetch('/cart/change.js', { id: write.id, quantity: write.quantity });
      });
    }, Promise.resolve());
  }

  /* The drawer re-renders itself after every write it can see, so only a
     reconcile that wrote nothing can leave it stale: the page was rendered
     with a gift line that an earlier document's removal has since taken out
     (the /cart -> /?viewcart=true hop with the change in flight), or the
     other way round. Ask the drawer to re-render when the rows disagree. */
  function syncDrawerRows(cart) {
    var drawerHasGift = !!document.querySelector('cart-drawer .wg-cart__item--gift');
    var cartHasGift = cart.items.some(isGiftLine);
    if (drawerHasGift === cartHasGift) return;
    if (window.WGCartDrawer && typeof window.WGCartDrawer.refresh === 'function') window.WGCartDrawer.refresh();
  }

  function reconcileOnce(hint) {
    var first = hint && Array.isArray(hint.items) ? Promise.resolve(hint) : readCart();
    return first
      .then(function (cart) {
        var writes = plan(cart);
        if (!writes.length) {
          syncDrawerRows(cart);
          return cart;
        }
        return apply(writes)
          .catch(function (error) {
            console.warn('[cart-gift]', error);
          })
          .then(readCart);
      })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('wg:cart', { detail: cart }));
        return cart;
      });
  }

  /* One reconcile at a time; callers get the cart the run ended with. */
  function reconcile(hint) {
    var run = function () {
      return resolveRegion().then(function () {
        return reconcileOnce(hint);
      });
    };
    chain = chain.then(run, run);
    return chain;
  }

  function scheduleReconcile() {
    clearTimeout(pendingTimer);
    pendingTimer = setTimeout(function () { reconcile(); }, 60);
  }

  function isCartWrite(url, method) {
    return method === 'POST' && /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url || '');
  }

  function watchFetch() {
    if (!window.fetch || window.fetch.__wgGiftWatch) return;
    var original = window.fetch;
    var wrapped = function (input, init) {
      var url = typeof input === 'string' ? input : input && input.url ? input.url : '';
      var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      var own = false;
      try {
        var headers = init && init.headers;
        if (headers && typeof headers.get === 'function') own = headers.get('X-WG-Gift') === '1';
        else if (headers) own = headers['X-WG-Gift'] === '1';
      } catch (e) {
        own = false;
      }
      var result = original.apply(this, arguments);
      if (!own && isCartWrite(url, method)) {
        result.then(scheduleReconcile, function () {});
      }
      return result;
    };
    wrapped.__wgGiftWatch = true;
    window.fetch = wrapped;
  }

  function watchXhr() {
    var proto = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
    if (!proto || proto.__wgGiftWatch) return;
    var open = proto.open;
    var send = proto.send;
    proto.open = function (method, url) {
      this.__wgCartWrite = isCartWrite(String(url), String(method || 'GET').toUpperCase());
      return open.apply(this, arguments);
    };
    proto.send = function () {
      if (this.__wgCartWrite) this.addEventListener('loadend', scheduleReconcile);
      return send.apply(this, arguments);
    };
    proto.__wgGiftWatch = true;
  }

  /* Gift line controls rendered by the drawer: swap variant, decline. */
  function bindControls() {
    document.addEventListener('click', function (event) {
      var target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      var swap = target.closest('[data-wg-gift-swap]');
      if (swap) {
        event.preventDefault();
        var toVariant = parseInt(swap.getAttribute('data-wg-gift-swap'), 10);
        var key = swap.getAttribute('data-wg-gift-key');
        if (!toVariant || !key) return;
        chain = chain.then(function () {
          var props = {};
          props._wg_gift = config.key;
          return ownFetch('/cart/change.js', { id: key, quantity: 0 })
            .then(function () {
              return ownFetch('/cart/add.js', { items: [{ id: toVariant, quantity: 1, properties: props }] });
            })
            .then(readCart)
            .then(function (cart) {
              document.dispatchEvent(new CustomEvent('wg:cart', { detail: cart }));
              return cart;
            });
        });
        return;
      }
      var decline = target.closest('[data-wg-gift-decline]');
      if (decline) {
        event.preventDefault();
        setDeclined(true);
        reconcile();
        return;
      }
      /* /cart page: Dawn's remove button on the gift line does the write
         itself; remember the decline so the reconcile it triggers does not
         put the gift straight back. */
      if (target.closest('[data-wg-gift-remove]')) setDeclined(true);
    });
  }

  window.WGCartGift = {
    config: config,
    eligibleSubtotal: eligibleSubtotal,
    isGiftLine: isGiftLine,
    reconcile: reconcile,
    plan: plan,
    declined: declined,
    undecline: function () {
      setDeclined(false);
      return reconcile();
    },
    regionAllowed: undefined,
    region: resolveRegion,
  };

  watchFetch();
  watchXhr();
  bindControls();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', reconcile);
  else reconcile();
})();
